import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { auditLogsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Shield,
  Search,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  User,
  FileText,
  Clock,
  Activity,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const ACTION_COLORS = {
  document_uploaded: 'bg-blue-100 text-blue-700',
  document_deleted: 'bg-red-100 text-red-700',
  document_approved: 'bg-emerald-100 text-emerald-700',
  document_reviewed: 'bg-amber-100 text-amber-700',
  document_reprocessed: 'bg-violet-100 text-violet-700',
  ocr_complete: 'bg-teal-100 text-teal-700',
  login: 'bg-indigo-100 text-indigo-700',
  generate_payroll_packet: 'bg-sky-100 text-sky-700',
  payroll_packet_generated: 'bg-sky-100 text-sky-700',
  deliverable_uploaded: 'bg-cyan-100 text-cyan-700',
  deliverable_deleted: 'bg-red-100 text-red-700',
  email_intake_processed: 'bg-violet-100 text-violet-700',
  create: 'bg-emerald-100 text-emerald-700',
};

const AuditLogsPage = () => {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    search: '',
    days: 30,
  });
  const [page, setPage] = useState(0);
  const [actionOptions, setActionOptions] = useState({ actions: [], resource_types: [] });
  const [summary, setSummary] = useState(null);
  const limit = 20;

  useEffect(() => {
    if (!tenantId) return;
    auditLogsAPI.getActions(tenantId).then((res) => setActionOptions(res.data));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    auditLogsAPI.getSummary(tenantId, 7).then((res) => setSummary(res.data));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    loadLogs();
  }, [tenantId, page, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        tenant_id: tenantId,
        skip: page * limit,
        limit,
        days: filters.days,
      };
      if (filters.action) params.action = filters.action;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.search) params.search = filters.search;
      const res = await auditLogsAPI.list(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Failed to load audit logs:', e);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const topActions = useMemo(() => {
    if (!summary?.actions_breakdown) return [];
    return Object.entries(summary.actions_breakdown)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4);
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
          <Shield className="h-8 w-8 text-indigo-600" />
          Audit Logs
        </h1>
        <p className="text-slate-500 mt-1">Track all actions and changes across the platform.</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-indigo-50">
                  <Activity className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900" data-testid="audit-total-events">{summary.total_events}</p>
                  <p className="text-xs text-slate-500">Events (7 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {topActions.map(([action, count]) => (
            <Card key={action}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-slate-50">
                    {action === 'login' ? <User className="h-5 w-5 text-slate-600" /> :
                     action.includes('document') ? <FileText className="h-5 w-5 text-slate-600" /> :
                     <Clock className="h-5 w-5 text-slate-600" />}
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-slate-900">{count}</p>
                    <p className="text-xs text-slate-500 capitalize">{action.replaceAll('_', ' ')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search logs..."
                className="pl-9 bg-slate-50"
                value={filters.search}
                onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(0); }}
                data-testid="audit-search-input"
              />
            </div>
            <Select value={filters.action} onValueChange={(v) => { setFilters((f) => ({ ...f, action: v === 'all' ? '' : v })); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionOptions.actions.map((a) => (
                  <SelectItem key={a} value={a}>{a.replaceAll('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.resource_type} onValueChange={(v) => { setFilters((f) => ({ ...f, resource_type: v === 'all' ? '' : v })); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="audit-resource-filter">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {actionOptions.resource_types.map((r) => (
                  <SelectItem key={r} value={r}>{r.replaceAll('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(filters.days)} onValueChange={(v) => { setFilters((f) => ({ ...f, days: parseInt(v) })); setPage(0); }}>
              <SelectTrigger className="w-[140px]" data-testid="audit-days-filter">
                <SelectValue placeholder="Last 30 days" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadLogs} data-testid="audit-refresh-btn">
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No audit logs found</p>
              <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Time</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Action</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Resource</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                      <div title={log.created_at && format(new Date(log.created_at), 'PPpp')}>
                        {log.created_at && formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                          {(log.user_name || log.user_email || log.user_id || '?')[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{log.user_name || log.user_email || log.user_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'} border-0 text-xs`}>
                        {log.action?.replaceAll('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="text-slate-600 capitalize">{log.resource_type?.replaceAll('_', ' ')}</span>
                        {log.resource_id && (
                          <span className="text-slate-400 text-xs ml-1 font-mono">
                            {log.resource_id.length > 12 ? `${log.resource_id.slice(0, 12)}...` : log.resource_id}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[250px]">
                      {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 ? (
                        <div className="text-xs text-slate-500 truncate font-mono" title={JSON.stringify(log.details)}>
                          {Object.entries(log.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">
                Page {page + 1} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AuditLogsPage;
